<script lang="ts" setup>
const { $t, $switchRoute } = useI18n()
useHead({ title: String($t('components.bitbeli.cryptoExchange.head')) })

const { swapFrom, swapTo } = storeToRefs(useSwapStore())
const { isLogged } = storeToRefs(useUser())

const [individualsHovered, toggleIndividualsHovered] = useToggle(false)
const buttonVariant = useState<'primary' | 'accent'>('buttonVariant')

onMounted (() => buttonVariant.value = 'accent')
onUnmounted (() => buttonVariant.value = 'primary')
</script>

<template>
  <div>
    <!-- hero section -->
    <div class="bg-gradient-to-br" from="accent-200 dark:neutral-50" via-accent-50 dark="bg-gradient-to-b via-50% via-accent-100/50" to="accent-200 dark:neutral-100" min-h-500px relative>
      <div fubex-container pt-28px pb-96px>
        <div bg="accent-300/40 dark:accent-100/30" w-fit mx-auto flex flex-row justify-center items-center gap-8px fubex-rounded-lg px-8px md:px-12px py-4px md:py-8px my-16px mb-60px>
          <div>
            <I18nLink to="/" hover="bg-primary-500 dark:bg-primary-300 text-white" py-8px px-16px fubex-rounded-md block duration-350 @mouseenter="toggleIndividualsHovered()" @mouseleave="toggleIndividualsHovered()">
              <span hidden sm:block>{{ $t('components.bitbeli.index.solutionsForCompanies') }}</span>
              <span block sm:hidden>{{ $t('components.bitbeli.index.companies') }}</span>
            </I18nLink>
          </div>
          <div :class="individualsHovered ? 'dark:text-white' : 'bg-accent-500 dark:bg-accent-300 text-black'" py-8px px-16px fubex-rounded-md cursor-default duration-350>
            <span hidden sm:block>{{ $t('components.bitbeli.index.servicesForIndividuals') }}</span>
            <span block sm:hidden>{{ $t('components.bitbeli.index.individuals') }}</span>
          </div>
        </div>
        <div flex flex-col xl:flex-row items-center gap-80px>
          <div flex flex-col gap-40px my-40px>
            <h1 display-sm md:display-lg font-700 text-center xl:text-left>
              {{ $t('components.bitbeli.cryptoExchange.heroTitle') }}
            </h1>
            <p>{{ $t('components.bitbeli.cryptoExchange.heroDescription') }}</p>
            <div flex flex-col sm:flex-row gap-x-8px gap-y-0px text-24px font-500 self-center xl:self-start>
              <div flex items-center gap-8px hover:bg-accent-200 dark:hover:bg-accent-100 rounded-lg>
                <UnoIcon i-fubex-stopwatch inline-block text-accent-500 dark:text-accent-300 h-30px w-30px />{{ $t('components.bitbeli.index.quickly') }}
              </div>
              <div flex items-center gap-8px hover:bg-accent-200 dark:hover:bg-accent-100 rounded-lg>
                <UnoIcon i-fubex-lightning inline-block text-accent-500 dark:text-accent-300 h-28px w-28px mx-3px />{{ $t('components.bitbeli.index.simply') }}
              </div>
              <div flex items-center gap-8px hover:bg-accent-200 dark:hover:bg-accent-100 rounded-lg>
                <UnoIcon i-fubex-safe inline-block text-accent-500 dark:text-accent-300 h-26px w-26px mx-1px />{{ $t('components.bitbeli.index.safely') }}
              </div>
            </div>
          </div>
          <div md:min-w="567px">
            <div
              :data-test="getCypressId('swap')"
              md:basis="1/2"
            >
              <div
                bg-white dark:bg-neutral-100 px-16px pt-32px pb-48px
                fubex-rounded-lg
                border-1 border-neutral-300
                md="px-32px pt-40px pb-32px rounded-br-90px" relative
                fubex-shadow-lg
              >
                <div bg-accent-200 px-16px py-8px fubex-rounded-lg paragraph-sm w-fit font-500 mb-32px>
                  {{ $t('components.bitbeli.cryptoExchange.usedByThousands') }}
                </div>
                <WidgetSwap :force-narrow="true" />
                <div flex flex-col sm:flex-row md:gap-32px mt-12px>
                  <FButton v-if="isLogged" name="swap-start" my-24px size="lg" w-full @click="$switchRoute('/profile/exchange')">
                    {{ $t('components.bitbeli.cryptoExchange.startExchange') }} {{ swapTo?.code }}
                  </FButton>
                  <WidgetAuthDialogs v-else default-view="login">
                    <FButton name="swap-start-auth" my-24px size="lg" w-full variant="accent">
                      {{ swapTo?.type === 'CRYPTO' ? $t('components.bitbeli.cryptoExchange.buyCrypto', { code: String(swapTo.code) }) : $t('components.bitbeli.cryptoExchange.sellCrypto', { code: String(swapFrom?.code) }) }}
                    </FButton>
                  </WidgetAuthDialogs>
                  <div flex justify-center gap-12px items-center paragraph-xs>
                    <UnoIcon i-fubex-dot text-success-500 h-10px w-10px />
                    {{ $t('components.bitbeli.cryptoExchange.takeAbout2Minutes') }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- /hero section -->

    <!-- token carousel -->
    <div>
      <WidgetTokenCarousel />
    </div>
    <!-- /token carousel -->
    <!-- crypto payments -->
    <div fubex-container my-100px flex flex-col gap-32px>
      <h2 heading2 md:heading1 font-700 text-center relative z-10>
        {{ $t('components.bitbeli.cryptoExchange.cryptoExchangeQuickSafe') }}
      </h2>
      <p paragraph-lg text-center max-w-4xl mx-auto relative z-10>
        {{ $t('components.bitbeli.cryptoExchange.cryptoPaymentsDescription') }}
      </p>
      <div grid grid-cols-1 md:grid-cols-2 items-center>
        <div flex flex-col gap-20px relative z-10>
          <h2 heading3 font-700>
            {{ $t('components.bitbeli.cryptoExchange.buyAndSell') }}
          </h2>
          <p>{{ $t('components.bitbeli.cryptoExchange.buildPortfolio') }}</p>
          <ul flex flex-col gap-8px>
            <LiWithCheck>
              {{ $t('components.bitbeli.cryptoExchange.digitalTransformation') }}
            </LiWithCheck>
            <LiWithCheck>
              {{ $t('components.bitbeli.cryptoExchange.investFuture') }}
            </LiWithCheck>
            <LiWithCheck>
              {{ $t('components.bitbeli.cryptoExchange.keepUpNewEra') }}
            </LiWithCheck>
          </ul>
          <div flex flex-col md:flex-row items-center gap-24px my="20px">
            <WidgetAuthDialogs default-view="register">
              <FButton name="create-account" size="lg" variant="accent">
                {{ $t('components.bitbeli.cryptoExchange.createAccount') }}
              </FButton>
            </WidgetAuthDialogs>
            <I18nLink to="/kurzovni-listek" basic-link>
              {{ $t('components.bitbeli.cryptoExchange.exchangeRates') }}
            </I18nLink>
          </div>
        </div>
        <div relative w="80%" h-full mt-80px mx-auto>
          <Ellipse variant="accent" />
          <NuxtImg src="/img/overlimit-man.webp" w-full z-10 relative />
        </div>
      </div>
    </div>
    <!-- /crypto payments -->

    <!-- benefits -->
    <div fubex-container my-100px relative z-10>
      <WidgetBenefitsIndividuals />
    </div>
    <!-- /benefits -->

    <!-- DCA -->
    <div id="pravidelne-sporeni" fubex-container my-100px flex flex-col gap-32px>
      <div grid grid-cols-1 md:grid-cols-2 items-center>
        <div flex flex-col gap-20px z-10>
          <h2 heading3 font-700>
            {{ $t('components.bitbeli.cryptoExchange.regularSaving') }}
          </h2>
          <p>{{ $t('components.bitbeli.cryptoExchange.regularSavingDescription') }}</p>
          <ul flex flex-col gap-8px>
            <LiWithCheck>
              {{ $t('components.bitbeli.cryptoExchange.optimalPrice') }}
            </LiWithCheck>
            <LiWithCheck>
              {{ $t('components.bitbeli.cryptoExchange.automatePurchases') }}
            </LiWithCheck>
            <LiWithCheck>
              {{ $t('components.bitbeli.cryptoExchange.researchShows') }}
            </LiWithCheck>
          </ul>
          <div flex flex-col md:flex-row items-center gap-24px my="20px">
            <WidgetAuthDialogs default-view="register">
              <FButton name="create-account" size="lg" variant="accent">
                {{ $t('components.bitbeli.cryptoExchange.createAccount') }}
              </FButton>
            </WidgetAuthDialogs>
          </div>
        </div>
        <div relative md:order-first w="80%" h-full mt-80px mx-auto>
          <Ellipse variant="accent" />
          <NuxtImg src="/img/crypto-exchange.webp" z-10 relative />
        </div>
      </div>
    </div>
    <!-- /DCA -->

    <!-- newsletter -->
    <div :data-test="getCypressId('newsletter')" fubex-container p-16px mb-48px>
      <WidgetNewsletter variant="accent" />
    </div>
    <!-- /newsletter -->

    <!-- Affiliate banner -->
    <div fubex-container max-w-7xl my-50px flex flex-col gap-32px>
      <div grid grid-cols-1 md:grid-cols-2 text-center items-center md:text-left>
        <div flex flex-col gap-32px z-10>
          <h2 heading2 font-700 v-html="$t('components.bitbeli.cryptoExchange.affiliateText')" />
          <p heading6 v-html="$t('components.bitbeli.cryptoExchange.joinCommunity')" />
          <div flex items-center justify-evenly md:justify-start space-x-2 gap-16px>
            <WidgetAuthDialogs default-view="register">
              <FButton name="hero-register" size="lg" w-full md:w-auto prefix-icon="i-fubex-user" variant="dark" class="px-8">
                {{ $t('components.bitbeli.cryptoExchange.createAccount') }}
              </FButton>
            </WidgetAuthDialogs>
            <I18nLink to="/affiliate">
              <FButton name="route-to-affiliate" variant="text" suffix-icon="i-fubex-chevron-right">
                {{ $t('components.bitbeli.cryptoExchange.moreInfo') }}
              </FButton>
            </I18nLink>
          </div>
        </div>
        <div relative w="80%" h-full mt-80px mx-auto>
          <Ellipse variant="accent" />
          <NuxtImg src="/img/bitcoin-img.webp" z-10 relative />
        </div>
      </div>
    </div>

    <!-- Affiliate banner -->

    <!-- contact form -->
    <WidgetContactForm variant="accent" relative z-10>
      <h2 heading2 font-700 text-white>
        {{ $t('components.bitbeli.cryptoExchange.contactForm') }}
      </h2>
    </WidgetContactForm>
    <!-- /contact form -->
  </div>
</template>

<style scoped>
:root:not(.dark) .bg-gradient-to-br {
  --un-gradient-shape: to bottom right in srgb;
}

[dark~="bg-gradient-to-b"] {
  --un-gradient-shape: to bottom in srgb;
}
</style>
